import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, InfoIcon, MailIcon, PackageSearchIcon, BadgeCheckIcon } from 'lucide-react';
import { quickStartSteps } from '../data/quickStart';
import { trackCustom } from '../utils/tracking';
interface QuickStartGuideProps {
  /** Section on the home page uses h2; the standalone /guide page renders its own h1. */
  headingLevel?: 'h1' | 'h2';
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function QuickStartGuide({
  headingLevel = 'h2'
}: QuickStartGuideProps) {
  const [index, setIndex] = useState(0);
  const reduceMotion = useReducedMotion();
  const step = quickStartSteps[index];
  const Heading = headingLevel;
  const go = (next: number) => {
    const clamped = Math.max(0, Math.min(quickStartSteps.length - 1, next));
    setIndex(clamped);
    trackCustom('quick_start_step_viewed', {
      step: quickStartSteps[clamped].id
    });
  };
  return <section id="guide" aria-labelledby="guide-heading" className="scroll-mt-28 border-b border-white/10 bg-ink-950" data-id="element-1148" data-mp="qdaei5t51m9z">
      <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-1149" data-mp="lga45ip99xd2">
        <p className="font-display text-xl font-extrabold uppercase tracking-wide text-grit-500" data-id="element-1150" data-mp="z7faf3n4pnwd">Quick start guide</p>
        <Heading id="guide-heading" className="grit-headline mt-3 text-[clamp(2.25rem,7vw,4rem)] font-extrabold uppercase text-offwhite" data-id="element-1151" data-mp="uevnhg2jxgew">
          How to use your X1.
        </Heading>
        <p className="mt-3 max-w-2xl text-lg text-steel-300" data-id="element-1152" data-mp="1458g7vkisfw4">Up and running in minutes. Tap a step.</p>

        <div className="mt-8 grid gap-px bg-white/10 lg:grid-cols-[300px_1fr]" data-id="element-1153" data-mp="1i35dyvapmscd">
          {/* Step list: a scrollable row on phones, a column on desktop */}
          <div className="bg-ink-950 lg:bg-ink-900" data-id="element-1154" data-mp="3jvupmn1bztgb">
            <ol aria-label="Quick start steps" className="flex gap-2 overflow-x-auto p-3 [-ms-overflow-style:none] [scrollbar-width:none] lg:flex-col lg:gap-0 lg:overflow-visible lg:p-0 [&::-webkit-scrollbar]:hidden" data-id="element-1155" data-mp="3jyfpx7lze1by">
              {quickStartSteps.map((item, i) => {
              const active = i === index;
              return <li key={item.id} className="shrink-0 lg:border-b lg:border-white/10" data-id="element-1156" data-mp="1sr9jr6owkmy4" data-mpi={i}>
                    <button type="button" onClick={() => go(i)} aria-current={active ? 'step' : undefined} className={`flex w-full items-center gap-3 whitespace-nowrap px-3 py-2.5 text-left transition-colors duration-150 ease-grit focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-grit-500 lg:px-5 lg:py-4 ${active ? 'bg-grit-500 text-ink-950' : 'border border-white/15 text-steel-300 hover:text-offwhite lg:border-0 lg:hover:bg-white/[0.04]'}`} data-id="element-1157" data-mp="1xt8l6b1319sf">
                      <span className={`flex h-7 w-7 shrink-0 items-center justify-center font-display text-base font-extrabold ${active ? 'bg-ink-950 text-grit-500' : 'bg-white/10 text-offwhite'}`} data-id="element-1158" data-mp="dx1iw9tw31sn">
                        {i + 1}
                      </span>
                      <span className="font-display text-base font-bold uppercase tracking-wide lg:text-lg" data-id="element-1159" data-mp="xjz64gq93tzg">{item.short}</span>
                    </button>
                  </li>;
            })}
            </ol>
          </div>

          {/* Active step */}
          <div className="bg-ink-950 p-5 sm:p-7" aria-live="polite" data-id="element-1160" data-mp="nrbahpb0bw2b">
            <AnimatePresence mode="wait" initial={false} data-id="element-1161" data-mp="iu1nshf05yi6">
              <motion.div key={step.id} initial={reduceMotion ? false : {
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={reduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.18,
              ease: EASE
            }} className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:items-start" data-id="element-1162" data-mp="1gagy2j39eljr">
                <div className="aspect-[4/3] w-full overflow-hidden border border-white/10 bg-ink-900" data-id="element-1163" data-mp="m42ve3553qyn">
                  <img src={step.image} alt={step.imageAlt} className="h-full w-full object-cover" data-id="element-1164" data-mp="r2xgjbqkxrlx" />
                </div>
                <div data-id="element-1165" data-mp="1esi82j0stx6t">
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-steel-400" data-id="element-1166" data-mp="1jqtk90ekgjs7">
                    Step {index + 1} of {quickStartSteps.length}
                  </p>
                  <h3 className="mt-2 font-display text-3xl font-extrabold uppercase leading-none text-offwhite sm:text-4xl" data-id="element-1167" data-mp="14zsyxusq0ven">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-[16px] leading-relaxed text-steel-200" data-id="element-1168" data-mp="1xp9pso0p048r">{step.body}</p>

                  {step.list && <dl className="mt-5 divide-y divide-white/10 border-y border-white/10" data-id="element-1169" data-mp="3n7q4i7vkw0xt">
                      {step.list.map(row => <div key={row.label} className="grid grid-cols-[96px_1fr] gap-3 py-2.5" data-id="element-1170" data-mp="3i7u0i8mvs9i9">
                          <dt className="font-display text-lg font-bold uppercase text-grit-500" data-id="element-1171" data-mp="3vyyv2wbe6blq">{row.label}</dt>
                          <dd className="text-[14px] leading-relaxed text-steel-300" data-id="element-1172" data-mp="3ryyg9akdrye5">{row.detail}</dd>
                        </div>)}
                    </dl>}

                  {step.tip && <p className="mt-5 flex items-start gap-2.5 border-l-4 border-grit-500 bg-ink-900 px-4 py-3 text-[14px] leading-relaxed text-steel-200" data-id="element-1173" data-mp="2zc3tu8406oy1">
                      <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1174" data-mp="389o1pzwdfjhm" />
                      {step.tip}
                    </p>}

                  <div className="mt-6 flex gap-3" data-id="element-1175" data-mp="nkzwro0y6e52">
                    <button type="button" onClick={() => go(index - 1)} disabled={index === 0} className="inline-flex items-center gap-2 border-2 border-white/20 px-4 py-2.5 font-display text-base font-bold uppercase tracking-wide text-offwhite transition-colors duration-150 ease-grit hover:border-grit-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-1176" data-mp="nmkshgz0034i">
                      <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1177" data-mp="32o3ea98t7hm1" />
                      Back
                    </button>
                    {index < quickStartSteps.length - 1 ? <button type="button" onClick={() => go(index + 1)} className="inline-flex items-center gap-2 bg-grit-500 px-5 py-2.5 font-display text-base font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-1178" data-mp="3cdvd5fnxywwb">
                        Next step
                        <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1179" data-mp="22dakwtvvu0tx" />
                      </button> : <Link to="/activate" className="inline-flex items-center gap-2 bg-grit-500 px-5 py-2.5 font-display text-base font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-1180" data-mp="1te5mdnkbm7hr">
                        Activate your X1
                        <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1181" data-mp="1aq0ouxu9bire" />
                      </Link>}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* After the guide: owner help */}
        <ul className="mt-6 grid gap-px bg-white/10 sm:grid-cols-3" data-id="element-1182" data-mp="2yi9kbphvicsa">
          {[{
          icon: BadgeCheckIcon,
          title: 'Activate your X1',
          body: 'Register it in under a minute.',
          to: '/activate'
        }, {
          icon: PackageSearchIcon,
          title: 'Track my order',
          body: 'Follow your package on USPS.',
          to: '/track'
        }, {
          icon: MailIcon,
          title: 'Need help?',
          body: 'support@gritvuegear.com',
          href: 'mailto:support@gritvuegear.com'
        }].map(item => {
          const Icon = item.icon;
          const inner = <>
                <Icon className="h-6 w-6 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1183" data-mp="25wq5kxy2av8k" />
                <span data-id="element-1184" data-mp="1rx8ak0u6alqr">
                  <span className="block font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1185" data-mp="1rwp178il95oc">{item.title}</span>
                  <span className="mt-0.5 block text-[13px] text-steel-400" data-id="element-1186" data-mp="1wx35cfh2jvh3">{item.body}</span>
                </span>
              </>;
          const cls = 'flex h-full items-center gap-3 bg-ink-900 p-4 transition-colors duration-150 ease-grit hover:bg-ink-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-grit-500';
          return <li key={item.title} data-id="element-1187" data-mp="1w8cfj5q70qjv">
                {item.to ? <Link to={item.to} className={cls} data-id="element-1188" data-mp="1w6hvruqnr4hd">
                    {inner}
                  </Link> : <a href={item.href} className={cls} data-id="element-1189" data-mp="25y5r46qc55o4">
                    {inner}
                  </a>}
              </li>;
        })}
        </ul>
      </div>
    </section>;
}